/**
 * 認証コンテキスト（Cookie ベース版）
 *
 * 【改修前との違い】
 *
 * 改修前:
 * - localStorage にユーザー情報を JSON で保存
 * - fetchEmployeeByCode() でスタッフコードだけでログイン
 * - パスワード認証なし
 * - トークン管理なし
 *
 * 改修後:
 * - localStorage を使わない（ユーザー情報は Redux Store に保持）
 * - RTK Query の useLoginMutation() でサーバー認証
 * - パスワード認証あり
 * - トークンは HttpOnly Cookie で管理（JS からアクセスできない）
 * - アプリ起動時は /auth/me でサーバーに「まだログイン有効？」を確認
 * - 5分ごとにポーリングしてセッション有効性をチェック
 *
 * 【このファイルの役割】
 * Redux Store（authSlice）のデータを React コンポーネントに配信する「窓口」。
 * データの実体は Redux Store に、配信は AuthContext に、と役割分担している。
 *
 * useAuth() を呼ぶだけで user, isAuthenticated, isLoading, logout, hasPermission が取れる。
 */

import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetMeQuery, useLogoutMutation } from '../shared/api/endpoints/authApi';
import { useAppDispatch, useAppSelector } from '../app/store';
import { setUser, logout as logoutAction } from '../shared/store/slices/authSlice';
import { api } from '../shared/store/api';

// ---------- 型定義 ----------

/**
 * User 型（既存コードとの互換性を維持）
 *
 * 既存コードでは user.storeId, user.departmentId, user.permissionLevel 等を
 * 使っているため、プロパティ名は変えずにデータソースだけ変える。
 */
export interface User {
  id: string;
  code: string;
  username: string;
  employeeId: number;
  tenantId: string;
  tenantCode: string;
  storeId: number;
  departmentId: number;
  permissionLevel: number;
  /** RBAC 権限レベル（permission_level） */
  rbacLevel: number;
  adminLevel: number;
  mustChangePassword: boolean;
  privacyConsentAt: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  /** ロールレベルによる権限チェック（Phase 3 で本格利用） */
  hasPermission: (level: number) => boolean;
}

// ---------- Context ----------

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ---------- Provider ----------

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoggedOut } = useAppSelector((state) => state.auth);
  const authUser = useAppSelector((state) => state.auth.user);

  const [logoutMutation] = useLogoutMutation();

  /**
   * アプリ起動時に GET /auth/me で認証状態を確認する。
   *
   * 【仕組み】
   * 1. ブラウザが Cookie を自動送信
   * 2. Cookie が有効 → ユーザー情報が返る → ログイン済み
   * 3. Cookie が無効 → 401 → baseQueryWithReauth がリフレッシュ試行
   * 4. リフレッシュ成功 → 新 Cookie → ユーザー情報が返る
   * 5. リフレッシュ失敗 → 401 → isError=true → 未認証
   *
   * pollingInterval: 5分ごとにサーバーと同期してセッション有効性を確認する。
   * 例：管理者がサーバー側でセッションを無効化した場合、
   * 最大5分以内にフロントが気づいてログイン画面に戻す。
   */
  const { data: meData, isLoading, isError, isFetching } = useGetMeQuery(undefined, {
    pollingInterval: 5 * 60 * 1000,
    skip: isLoggedOut,
  });

  // /auth/me のレスポンスを Redux Store に反映
  // isFetching ガード: ログイン直後に skip が解除されて再フェッチ中、
  // 前回の isError=true が残っているため、リフェッチ完了まで logout を遅延させる
  useEffect(() => {
    if (meData) {
      dispatch(setUser({
        id: meData.id,
        code: meData.code,
        name: meData.name,
        role: meData.role,
        permission_level: meData.permission_level,
        admin_level: meData.admin_level,
        must_change_password: meData.must_change_password,
        privacy_consent_at: meData.privacy_consent_at,
      }));
    }
    if (isError && !isFetching) {
      dispatch(logoutAction());
    }
  }, [meData, isError, isFetching, dispatch]);

  /**
   * Redux Store の authUser を、既存コード互換の User 型に変換する。
   *
   * 【なぜ変換が必要？】
   * - Redux Store: { id: 123, name: "山田", role: 3, admin_level: 1 }
   * - 既存コード: { employeeId: 123, username: "山田", permissionLevel: 3, adminLevel: 1 }
   * プロパティ名が違うので、既存コードが壊れないように変換する。
   */
  const user: User | null = useMemo(() => {
    if (!authUser || !meData) return null;
    return {
      id: String(authUser.id),
      code: authUser.code,
      username: authUser.name,
      employeeId: authUser.id,
      tenantId: meData.tenant_id,
      tenantCode: meData.tenant_code,
      storeId: meData.store_id,
      departmentId: meData.department_id,
      permissionLevel: authUser.permission_level,
      rbacLevel: authUser.permission_level,
      adminLevel: authUser.admin_level,
      mustChangePassword: authUser.must_change_password,
      privacyConsentAt: meData.privacy_consent_at ?? null,
    };
  }, [authUser, meData]);

  /**
   * ログアウト処理
   *
   * 【処理順序】
   * 1. POST /auth/logout でサーバー側のセッションを破棄
   *    → サーバーが Set-Cookie で Cookie を消す
   * 2. Redux Store の auth state をクリア
   * 3. RTK Query の全キャッシュをリセット
   *    → 他のユーザーのデータが残らないように
   * 4. localStorage のメニューキャッシュ等をクリア
   * 5. ログイン画面にリダイレクト
   *
   * 注意: API 呼び出しが失敗しても（例：ネットワーク切断）、
   * フロント側は必ずクリアする（セキュリティ上の安全側倒し）。
   */
  const logout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
    } catch {
      // ログアウト API が失敗してもフロント側はクリアする
    }
    dispatch(logoutAction());
    dispatch(api.util.resetApiState());
    // 既存コードで使っていた localStorage もクリア
    localStorage.removeItem('user');
    localStorage.removeItem('app_menus');
    localStorage.removeItem('login_code');
    navigate('/login', { replace: true });
  }, [logoutMutation, dispatch, navigate]);

  /** RBAC 権限レベルによる権限チェック */
  const hasPermission = useCallback(
    (level: number): boolean => (authUser?.permission_level ?? 0) >= level,
    [authUser],
  );

  // RTK Query の isLoading が false になっても、useEffect で setUser が
  // dispatch されるまで 1 レンダーサイクルの隙間がある。
  // この隙間で ProtectedRoute が「未認証」と判定して /login にリダイレクトしてしまう。
  // meData が既にあるのに isAuthenticated がまだ false = effect 処理待ち、なので
  // その間もローディング扱いにする。
  const effectiveIsLoading = isLoading || (!!meData && !isAuthenticated);

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated,
    isLoading: effectiveIsLoading,
    logout,
    hasPermission,
  }), [user, isAuthenticated, effectiveIsLoading, logout, hasPermission]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
